import React, { useEffect, useState } from "react";
import { FiAlertTriangle, FiCalendar, FiEdit3, FiFileText, FiPlus, FiX } from "react-icons/fi";
import AttendanceCard from "../components/AttendanceCard";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";
import ScreenMetric from "./ScreenMetric";

const getStatusClass = (request) => request.statusTone || String(request.status || "pending").toLowerCase().replace(/\s+/g, "-");

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parseLogDate = (log) => {
  const rawValue = log?.rawLoggedDate || log?.loggedDate;
  if (!rawValue) {
    return null;
  }

  const parsedDate = new Date(rawValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate;
  }

  const displayMatch = String(rawValue).match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (!displayMatch) {
    return null;
  }

  const monthIndex = monthNames.findIndex((month) => month.toLowerCase() === displayMatch[2].toLowerCase());
  return monthIndex >= 0 ? new Date(Number(displayMatch[3]), monthIndex, Number(displayMatch[1])) : null;
};

const getMonthKey = (date) =>
  date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
    : "unknown";

const formatLeaveBalance = (value) => {
  const balance = Number(value || 0);
  const normalizedBalance = Number(balance.toFixed(2));
  return `${normalizedBalance} ${normalizedBalance === 1 ? "day" : "days"}`;
};

const getMonthLabel = (date) =>
  date
    ? date.toLocaleDateString("en-IN", { month: "long", year: "numeric" })
    : "Unknown month";

const formatAttendanceHistoryDate = (row) =>
  row?.date
    ? row.date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "--";

const formatAttendanceWarningDate = (row) =>
  row?.date
    ? row.date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
    : "--";

const getUpcomingHoliday = (holidays = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return holidays.find((holiday) => {
    const rawDate = holiday.dateKey || holiday.rawDate || holiday.date;
    const holidayDate = new Date(String(rawDate).includes("T") ? rawDate : `${rawDate}T00:00:00`);
    return !Number.isNaN(holidayDate.getTime()) && holidayDate >= today;
  });
};

const toDateKey = (value) => {
  if (!value) {
    return "";
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return "";
    }
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  }

  const rawValue = String(value).trim();
  const isoMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }

  const displayMatch = rawValue.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/);
  if (displayMatch) {
    const monthIndex = monthNames.findIndex((month) => displayMatch[2].toLowerCase().startsWith(month.toLowerCase()));
    if (monthIndex >= 0) {
      return `${displayMatch[3]}-${String(monthIndex + 1).padStart(2, "0")}-${String(displayMatch[1]).padStart(2, "0")}`;
    }
  }

  const date = new Date(rawValue.includes("T") ? rawValue : `${rawValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const getMonthBounds = (monthKey) => {
  const [year, month] = String(monthKey || "").split("-").map(Number);
  const baseDate = Number.isFinite(year) && Number.isFinite(month)
    ? new Date(year, month - 1, 1)
    : new Date();
  return {
    startKey: toDateKey(baseDate),
    endKey: toDateKey(new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0)),
  };
};

const getCurrentMonthKey = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
};

const isApprovedStatus = (status) => String(status || "").toLowerCase().includes("approved");
const isPendingLeaveStatus = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus.includes("pending") || normalizedStatus.includes("review") || normalizedStatus.includes("submitted");
};

const isLossOfPayLeave = (request) => {
  const leaveType = String(request?.leaveType || request?.type || "").trim().toLowerCase();
  return leaveType === "lop" || leaveType.includes("loss of pay");
};

const getNextDateKey = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  date.setDate(date.getDate() + 1);
  return toDateKey(date);
};

const countWorkingDays = (startKey, endKey, holidayKeys) => {
  let total = 0;
  for (let dateKey = startKey; dateKey && dateKey <= endKey; dateKey = getNextDateKey(dateKey)) {
    const date = new Date(`${dateKey}T00:00:00`);
    const day = date.getDay();
    if (day !== 0 && day !== 6 && !holidayKeys.has(dateKey)) {
      total += 1;
    }
  }
  return total;
};

const calculateApprovedLopDaysForMonth = (leaveRequests = [], holidays = [], monthKey = getCurrentMonthKey()) => {
  const { startKey: monthStartKey, endKey: monthEndKey } = getMonthBounds(monthKey);
  const holidayKeys = new Set(holidays.map((holiday) => toDateKey(holiday?.dateKey || holiday?.rawDate || holiday?.date)).filter(Boolean));

  return leaveRequests.reduce((total, request) => {
    if (!isApprovedStatus(request?.status) || !isLossOfPayLeave(request)) {
      return total;
    }

    const requestStartKey = toDateKey(request.startDateKey || request.startDate || request.date);
    const requestEndKey = toDateKey(request.endDateKey || request.endDate || request.date || requestStartKey) || requestStartKey;
    if (!requestStartKey || !requestEndKey || requestStartKey > monthEndKey || requestEndKey < monthStartKey) {
      return total;
    }

    const leaveDays = Number(request.leaveDays || request.days || 0);
    if (leaveDays === 0.5 && requestStartKey >= monthStartKey && requestStartKey <= monthEndKey) {
      return total + 0.5;
    }

    const overlapStartKey = requestStartKey > monthStartKey ? requestStartKey : monthStartKey;
    const overlapEndKey = requestEndKey < monthEndKey ? requestEndKey : monthEndKey;
    const overlapWorkingDays = countWorkingDays(overlapStartKey, overlapEndKey, holidayKeys);
    const requestWorkingDays = countWorkingDays(requestStartKey, requestEndKey, holidayKeys);
    const cappedDays = leaveDays > 0 && requestWorkingDays > 0
      ? Math.min(overlapWorkingDays, leaveDays)
      : overlapWorkingDays;

    return total + cappedDays;
  }, 0);
};

const formatLeaveDays = (days) => {
  const rounded = Number(Number(days || 0).toFixed(2));
  return `${rounded} ${rounded === 1 ? "day" : "days"}`;
};

const ATTENDANCE_CODE_META = {
  P: { label: "Present", tone: "present" },
  "P/2": { label: "Present half day", tone: "present-half" },
  PL: { label: "Pending leave", tone: "pending-leave" },
  L: { label: "Leave", tone: "leave" },
  "L/2": { label: "Half-day leave", tone: "leave-half" },
  A: { label: "Absent", tone: "absent" },
  U: { label: "Unknown", tone: "unknown" },
};

const ATTENDANCE_HISTORY_START_DATE_KEY = "2026-05-01";
const ATTENDANCE_HISTORY_START_MONTH_KEY = ATTENDANCE_HISTORY_START_DATE_KEY.slice(0, 7);
const LEAVE_HISTORY_START_DATE_KEY = "2026-05-03";

const ATTENDANCE_LEGEND = [
  ["P", "Present"],
  ["PL", "Pending leave"],
  ["L", "Approved leave"],
  ["L/2", "Half-day leave"],
  ["A", "Absent"],
  ["U", "Unknown"],
];

const isHalfDayLeaveRequest = (request = {}) => {
  const duration = String(request.duration || request.durationType || "").toLowerCase();
  return Number(request.leaveDays || request.days || 0) === 0.5 || duration.includes("half");
};

const getAttendanceCodeFromLog = (log = {}) => {
  const rawCode = String(log.attendanceCode || log.code || "").trim().toUpperCase();
  const rawStatus = String(log.status || "").trim();
  const normalizedStatus = rawStatus.toLowerCase().replace(/[_\s-]+/g, "");
  const hasPresentSignal = Boolean(log.rawCheckIn || log.checkInTime || log.checkInAt);

  if (["P", "P/2", "PL", "L", "L/2", "A", "U"].includes(rawCode)) {
    return rawCode;
  }
  if (normalizedStatus.includes("pendingleave") || normalizedStatus === "pl") {
    return "PL";
  }
  if (normalizedStatus.includes("absent") || normalizedStatus === "a") {
    return "A";
  }
  if (normalizedStatus.includes("unknown") || normalizedStatus === "u") {
    return "U";
  }
  if (normalizedStatus.includes("halfdayleave") || normalizedStatus === "l/2") {
    return "L/2";
  }
  if (normalizedStatus.includes("leave") || normalizedStatus === "l") {
    return "L";
  }
  if (normalizedStatus.includes("present") || normalizedStatus.includes("completed") || normalizedStatus.includes("approved") || hasPresentSignal) {
    return "P";
  }
  return "";
};

const getAttendanceCodeMeta = (code) => ATTENDANCE_CODE_META[code] || ATTENDANCE_CODE_META.U;

const getAttendanceRowDateKey = (log = {}) =>
  toDateKey(log.dateKey || log.rawLoggedDate || log.loggedDate || log.date || log.attendanceDate);

const getLeaveCoverageByDate = (leaveRequests = [], holidays = []) => {
  const holidayKeys = new Set(holidays.map((holiday) => toDateKey(holiday?.dateKey || holiday?.rawDate || holiday?.date)).filter(Boolean));
  const coverageByDate = leaveRequests.reduce((coverage, request) => {
    const isApproved = isApprovedStatus(request?.status);
    const isPending = isPendingLeaveStatus(request?.status);
    if (!isApproved && !isPending) {
      return coverage;
    }

    const startKey = toDateKey(request.startDateKey || request.startDate || request.date);
    const endKey = toDateKey(request.endDateKey || request.endDate || request.date || startKey) || startKey;
    if (!startKey || !endKey) {
      return coverage;
    }

    const isHalfDay = isHalfDayLeaveRequest(request);
    const unitsPerDate = isHalfDay ? 0.5 : 1;
    for (let dateKey = startKey; dateKey && dateKey <= endKey; dateKey = getNextDateKey(dateKey)) {
      const date = new Date(`${dateKey}T00:00:00`);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      if (!isHalfDay && (isWeekend || holidayKeys.has(dateKey))) {
        continue;
      }
      const current = coverage.get(dateKey) || {
        approvedUnits: 0,
        pendingUnits: 0,
        request,
      };
      coverage.set(dateKey, {
        ...current,
        request,
        approvedUnits: current.approvedUnits + (isApproved ? unitsPerDate : 0),
        pendingUnits: current.pendingUnits + (isPending ? unitsPerDate : 0),
      });
    }
    return coverage;
  }, new Map());

  return Array.from(coverageByDate.entries()).reduce((coverage, [dateKey, value]) => {
    const approvedUnits = Number(value.approvedUnits || 0);
    const pendingUnits = Number(value.pendingUnits || 0);
    coverage.set(dateKey, {
      code: pendingUnits > 0 ? "PL" : approvedUnits >= 1 ? "L" : "L/2",
      request: value.request,
    });
    return coverage;
  }, new Map());
};

export const getAttendanceMonthRows = (attendanceLogs = [], leaveRequests = [], holidays = [], monthKey = getCurrentMonthKey()) => {
  const { startKey, endKey } = getMonthBounds(monthKey);
  if (endKey < ATTENDANCE_HISTORY_START_DATE_KEY) {
    return [];
  }
  const boundedStartKey = startKey < ATTENDANCE_HISTORY_START_DATE_KEY ? ATTENDANCE_HISTORY_START_DATE_KEY : startKey;
  const todayKey = toDateKey(new Date());
  const cappedEndKey = monthKey === getCurrentMonthKey() && todayKey < endKey ? todayKey : endKey;
  const holidayKeys = new Set(holidays.map((holiday) => toDateKey(holiday?.dateKey || holiday?.rawDate || holiday?.date)).filter(Boolean));
  const leaveByDate = getLeaveCoverageByDate(leaveRequests, holidays);
  const logsByDate = attendanceLogs.reduce((logs, log) => {
    const dateKey = getAttendanceRowDateKey(log);
    if (!dateKey) {
      return logs;
    }
    if (!logs.has(dateKey)) {
      logs.set(dateKey, []);
    }
    logs.get(dateKey).push(log);
    return logs;
  }, new Map());
  const rows = [];

  for (let dateKey = boundedStartKey; dateKey && dateKey <= cappedEndKey; dateKey = getNextDateKey(dateKey)) {
    const date = new Date(`${dateKey}T00:00:00`);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const isHoliday = holidayKeys.has(dateKey);
    const dayLogs = logsByDate.get(dateKey) || [];
    const logCode = dayLogs.map(getAttendanceCodeFromLog).find(Boolean);
    const leaveCoverage = leaveByDate.get(dateKey);

    if ((isWeekend || isHoliday) && !dayLogs.length && !leaveCoverage) {
      continue;
    }

    let code = logCode || "U";
    if (leaveCoverage?.code === "PL") {
      code = "PL";
    } else if (leaveCoverage?.code === "L/2") {
      code = logCode === "P" || logCode === "P/2" ? "P/2" : "L/2";
    } else if (leaveCoverage?.code === "L") {
      code = "L";
    }

    const meta = getAttendanceCodeMeta(code);
    rows.push({
      id: `${dateKey}-${code}`,
      date,
      dateKey,
      code,
      label: meta.label,
      tone: meta.tone,
    });
  }

  return rows.sort((left, right) => right.dateKey.localeCompare(left.dateKey));
};

export const isActionableUnknownAttendanceRow = (row = {}, todayKey = toDateKey(new Date())) => {
  if (row.code !== "U") {
    return false;
  }

  const rowDateKey = toDateKey(row.dateKey || row.rawLoggedDate || row.loggedDate || row.date);
  return Boolean(rowDateKey && rowDateKey !== todayKey);
};

const LeaveScreen = ({
  attendance,
  attendanceMarkingAllowed = false,
  attendanceNetworkMessage = "",
  attendanceNetworkResolved = false,
  attendanceOpenRequest = 0,
  attendanceLoading = false,
  dataLoading = false,
  dataLoadingLabel = "Refreshing leave data",
  holidays = [],
  leaveBalances = [],
  leaveCredits = [],
  leaveRequests = [],
  onAction,
  onAttendanceToggle,
}) => {
  const [isHolidayCalendarOpen, setIsHolidayCalendarOpen] = useState(false);
  const [isAttendanceOpen, setIsAttendanceOpen] = useState(false);
  const [attendanceFilterLoading, setAttendanceFilterLoading] = useState(false);
  const totalRemaining = leaveBalances.reduce((sum, balance) => sum + Number(balance.remaining || 0), 0);
  const nextHoliday = getUpcomingHoliday(holidays);
  const pendingRequests = leaveRequests.filter((request) => request.status === "Pending");
  const approvedLopDays = calculateApprovedLopDaysForMonth(leaveRequests, holidays);
  const leaveRows = [
    ...leaveRequests.map((request) => ({
      ...request,
      amountLabel: request.duration || formatLeaveBalance(request.leaveDays),
      displayDate: request.date,
      displayEndDate: request.endDate,
      movementType: request.movementType || "Debit",
      sortDateKey: request.startDateKey || toDateKey(request.date),
    })),
    ...leaveCredits.map((credit) => ({
      ...credit,
      amountLabel: formatLeaveBalance(credit.creditedDays),
      displayDate: credit.date,
      displayEndDate: "",
      requestNumber: credit.creditNumber || credit.id,
      statusTone: "approved",
      movementType: credit.movementType || "Credit",
      sortDateKey: credit.dateKey || toDateKey(credit.date),
    })),
  ]
    .filter((row) => row.sortDateKey >= LEAVE_HISTORY_START_DATE_KEY)
    .sort((left, right) => {
      const dateOrder = right.sortDateKey.localeCompare(left.sortDateKey);
      return dateOrder || String(right.requestNumber || "").localeCompare(String(left.requestNumber || ""));
    });
  const todayKey = toDateKey(new Date());
  const attendanceKey = toDateKey(attendance?.rawLoggedDate || attendance?.loggedDate || attendance?.date);
  const isPresentToday = attendanceKey === todayKey && Boolean(attendance?.status === "checked-in" || attendance?.checkInAt || attendance?.checkInTime);
  const activeLog = isPresentToday
    ? {
        id: "active-attendance-log",
        rawLoggedDate: attendance.rawLoggedDate || new Date(),
        loggedDate: attendance.loggedDate,
        status: "Present",
      }
    : null;
  const attendanceLogs = [...(activeLog ? [activeLog] : []), ...(attendance?.logs || [])];
  const monthOptions = Array.from(
    [...attendanceLogs, ...leaveRequests, { startDateKey: todayKey }].reduce((options, item) => {
      const leaveDateKey = item?.startDateKey || item?.dateKey || item?.rawLoggedDate || item?.loggedDate || item?.date;
      if (leaveDateKey) {
        const leaveDate = new Date(String(leaveDateKey).includes("T") ? leaveDateKey : `${leaveDateKey}T00:00:00`);
        const leaveKey = getMonthKey(Number.isNaN(leaveDate.getTime()) ? parseLogDate(item) : leaveDate);
        if (!options.has(leaveKey)) {
          options.set(leaveKey, { key: leaveKey, label: getMonthLabel(Number.isNaN(leaveDate.getTime()) ? parseLogDate(item) : leaveDate) });
        }
        return options;
      }
      const date = parseLogDate(item);
      const key = getMonthKey(date);
      if (!options.has(key)) {
        options.set(key, { key, label: getMonthLabel(date) });
      }
      return options;
    }, new Map()).values()
  )
    .filter((option) => option.key !== "unknown" && option.key >= ATTENDANCE_HISTORY_START_MONTH_KEY)
    .sort((left, right) => right.key.localeCompare(left.key));
  const defaultMonth = monthOptions[0]?.key || "all";
  const [selectedAttendanceMonth, setSelectedAttendanceMonth] = useState(defaultMonth);
  const activeMonth = monthOptions.some((option) => option.key === selectedAttendanceMonth)
    ? selectedAttendanceMonth
    : defaultMonth;
  const attendanceHistoryRows = getAttendanceMonthRows(attendanceLogs, leaveRequests, holidays, activeMonth === "all" ? getCurrentMonthKey() : activeMonth);
  const unknownAttendanceRows = attendanceHistoryRows.filter((row) => isActionableUnknownAttendanceRow(row, todayKey));
  const unknownAttendanceDateText = unknownAttendanceRows
    .slice(0, 8)
    .map(formatAttendanceWarningDate)
    .join(", ");
  const hiddenUnknownDateCount = Math.max(unknownAttendanceRows.length - 8, 0);

  useEffect(() => {
    if (!attendanceFilterLoading) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setAttendanceFilterLoading(false), 250);
    return () => window.clearTimeout(timeoutId);
  }, [attendanceFilterLoading, activeMonth]);

  const handleAttendanceMonthChange = (monthKey) => {
    setAttendanceFilterLoading(true);
    setSelectedAttendanceMonth(monthKey);
  };

  const openAttendanceAction = (action) => {
    setIsAttendanceOpen(false);
    window.setTimeout(() => onAction(action), 0);
  };

  useEffect(() => {
    if (attendanceOpenRequest > 0) {
      setIsAttendanceOpen(true);
    }
  }, [attendanceOpenRequest]);

  return (
    <section className="new-hrm-screen">
      <ScreenHeader
        eyebrow="People"
        title="Leave and Attendance"
        description="Mark present first, then review leave and attendance records."
        actions={(
          <button className="new-hrm-primary-button" type="button" onClick={() => onAction("leave")} disabled={dataLoading}>
            <FiPlus aria-hidden="true" />
            <span>Request Leave</span>
          </button>
        )}
      />
      <AttendanceCard
        attendance={attendance}
        attendanceMarkingAllowed={attendanceMarkingAllowed}
        attendanceNetworkMessage={attendanceNetworkMessage}
        attendanceNetworkResolved={attendanceNetworkResolved}
        loading={attendanceLoading || dataLoading}
        loadingLabel={attendanceLoading ? "Saving attendance" : dataLoadingLabel}
        onToggle={onAttendanceToggle}
        onViewMore={() => setIsAttendanceOpen(true)}
      />
      <div className="new-hrm-screen-metrics new-hrm-screen-metrics--compact">
        <ScreenMetric
          label="Available"
          value={leaveBalances.length ? `${totalRemaining} days` : "--"}
          detail="Total remaining balance"
        />
        <ScreenMetric
          label="Pending"
          value={pendingRequests.length}
          detail="Awaiting approval"
        />
        <ScreenMetric
          label="Approved LOP"
          value={formatLeaveDays(approvedLopDays)}
          detail="This month deduction"
        />
        <ScreenMetric
          label="Next holiday"
          value={nextHoliday?.date || "--"}
          detail={nextHoliday?.name || "Holiday calendar"}
        />
      </div>
      <div className="new-hrm-leave-layout">
        <section className="new-hrm-panel new-hrm-leave-requests-panel new-hrm-loader-host">
          <ComponentLoader active={dataLoading} label={dataLoadingLabel} />
          <header className="new-hrm-panel__header new-hrm-panel__header--compact">
            <h2>Leaves:</h2>
            <button type="button" onClick={() => onAction("leave")} disabled={dataLoading}>Request Leave</button>
          </header>
          <div className="new-hrm-screen-list">
            {leaveRows.map((request) => {
              const isLeaveRequest = String(request.movementType || "").toLowerCase() === "debit";
              const isRejected = String(request.status || "").toLowerCase().includes("reject");
              const rowContent = (
                <>
                  <div>
                    <strong>{request.requestNumber}</strong>
                    <span>{request.leaveType}</span>
                  </div>
                  <div>
                    <strong>{request.amountLabel}</strong>
                    <span>{request.displayDate}{request.displayEndDate && request.displayEndDate !== request.displayDate ? ` - ${request.displayEndDate}` : ""}</span>
                  </div>
                  <em className={`new-hrm-leave-type-pill new-hrm-leave-type-pill--${String(request.movementType || "").toLowerCase()}`}>
                    {request.movementType}
                  </em>
                  <em className={`new-hrm-status new-hrm-status--${getStatusClass(request)}`}>{request.status}</em>
                  {request.canEdit && (
                    <span className="new-hrm-edit-cue" aria-hidden="true" title="Editable request">
                      <FiEdit3 />
                      <span>Edit</span>
                    </span>
                  )}
                  {isRejected && request.rejectionReason && (
                    <span className="new-hrm-leave-request-row__reason">Reason: {request.rejectionReason}</span>
                  )}
                </>
              );

              return isLeaveRequest ? (
                <button
                  key={`${request.movementType}-${request.id}`}
                  type="button"
                  className="new-hrm-screen-list__item new-hrm-leave-request-row new-hrm-leave-request-row--actionable"
                  onClick={() => onAction("leave", request)}
                  disabled={dataLoading}
                  aria-label={`${request.canEdit ? "Edit" : "View"} leave request ${request.requestNumber}`}
                >
                  {rowContent}
                </button>
              ) : (
                <div key={`${request.movementType}-${request.id}`} className="new-hrm-screen-list__item new-hrm-leave-request-row">
                  {rowContent}
                </div>
              );
            })}
            {!leaveRows.length && <p className="new-hrm-empty-state">No leave records found after 02 May 2026.</p>}
          </div>
        </section>

        <aside className="new-hrm-leave-side">
          <section className="new-hrm-panel new-hrm-loader-host">
            <ComponentLoader active={dataLoading} label={dataLoadingLabel} />
            <div className="new-hrm-holiday-summary">
              <div>
                <h3>Upcoming Holiday</h3>
                {nextHoliday ? (
                  <p>
                    <strong>{nextHoliday.name}</strong>
                    <span>{nextHoliday.date}</span>
                  </p>
                ) : (
                  <p className="new-hrm-empty-state">No holidays found.</p>
                )}
              </div>
              <button
                type="button"
                className="new-hrm-inline-link"
                onClick={() => setIsHolidayCalendarOpen(true)}
                disabled={!holidays.length}
              >
                <FiCalendar aria-hidden="true" />
                <span>Holiday calendar</span>
              </button>
            </div>
          </section>

          <section className="new-hrm-panel new-hrm-loader-host">
            <ComponentLoader active={dataLoading} label={dataLoadingLabel} />
            <h2>Balance Breakdown</h2>
            {leaveBalances.length ? (
              <table className="new-hrm-balance-table">
                <thead>
                  <tr>
                    <th>Leave Type</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveBalances.map((balance) => (
                    <tr key={balance.id}>
                      <td>{balance.label}</td>
                      <td>{formatLeaveBalance(balance.remaining)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="new-hrm-empty-state">No leave balance found.</p>
            )}
          </section>

        </aside>
      </div>
      {isHolidayCalendarOpen && (
        <div className="new-hrm-modal-backdrop" role="presentation">
          <section className="new-hrm-modal new-hrm-holiday-modal" role="dialog" aria-modal="true" aria-labelledby="holiday-calendar-title">
            <header className="new-hrm-modal__header">
              <div>
                <h2 id="holiday-calendar-title">Holiday Calendar</h2>
                <p>{holidays.length} company holidays</p>
              </div>
              <button
                type="button"
                className="new-hrm-icon-button"
                onClick={() => setIsHolidayCalendarOpen(false)}
                aria-label="Close holiday calendar"
              >
                <FiX aria-hidden="true" />
              </button>
            </header>
            <div className="new-hrm-holiday-modal__list">
              {holidays.map((holiday) => (
                <div key={`${holiday.date}-${holiday.name}`} className="new-hrm-holiday-modal__row">
                  <span>{holiday.date}</span>
                  <strong>{holiday.name}</strong>
                </div>
              ))}
              {!holidays.length && <p className="new-hrm-empty-state">No holidays found.</p>}
            </div>
          </section>
        </div>
      )}
      {isAttendanceOpen && (
        <div className="new-hrm-modal-backdrop" role="presentation">
          <section className="new-hrm-modal new-hrm-attendance-modal" role="dialog" aria-modal="true" aria-labelledby="attendance-history-title">
            <header className="new-hrm-modal__header">
              <div>
                <h2 id="attendance-history-title">Attendance History</h2>
                <p>Review attendance by month.</p>
              </div>
              <button
                type="button"
                className="new-hrm-icon-button"
                onClick={() => setIsAttendanceOpen(false)}
                aria-label="Close attendance history"
              >
                <FiX aria-hidden="true" />
              </button>
            </header>
            <div className="new-hrm-attendance-modal__toolbar">
              <label className="new-hrm-field new-hrm-attendance-month-field">
                <span>Month</span>
                <select value={activeMonth} onChange={(event) => handleAttendanceMonthChange(event.target.value)}>
                  {monthOptions.length ? (
                    monthOptions.map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))
                  ) : (
                    <option value="all">No attendance months</option>
                  )}
                </select>
              </label>
            </div>
            <div className="new-hrm-attendance-log new-hrm-attendance-log--modal new-hrm-loader-host">
              <ComponentLoader active={attendanceFilterLoading} label="Applying month filter" />
              <div className="new-hrm-attendance-legend" aria-label="Attendance code legend">
                {ATTENDANCE_LEGEND.map(([code, label]) => {
                  const meta = getAttendanceCodeMeta(code);
                  return (
                    <span key={code} className={`new-hrm-attendance-log__status new-hrm-attendance-log__status--${meta.tone}`}>
                      <span aria-hidden="true"></span>
                      <strong>{code}</strong>
                      <em>{label}</em>
                    </span>
                  );
                })}
              </div>
              {unknownAttendanceRows.length > 0 && (
                <div className="new-hrm-attendance-critical-warning" role="alert">
                  <FiAlertTriangle aria-hidden="true" />
                  <div>
                    <strong>Attendance violation. Immediate action required.</strong>
                    <p>
                      {unknownAttendanceRows.length} unknown attendance day{unknownAttendanceRows.length === 1 ? "" : "s"} found this month. Your record is non-compliant.
                      {unknownAttendanceDateText ? ` Unknown dates: ${unknownAttendanceDateText}${hiddenUnknownDateCount ? `, +${hiddenUnknownDateCount} more` : ""}.` : " Dates are unknown in the attendance record."}
                      Unresolved unknown attendance can be treated as absence or LOP and escalated for disciplinary action. Fix it now by applying leave, requesting correction, or contacting HR.
                    </p>
                  </div>
                </div>
              )}
              <div className="new-hrm-attendance-log__table">
                <div className="new-hrm-attendance-log__head">Date</div>
                <div className="new-hrm-attendance-log__head">Code</div>
                <div className="new-hrm-attendance-log__head">Action</div>
                {attendanceHistoryRows.length ? (
                  attendanceHistoryRows.map((row) => (
                    <React.Fragment key={row.id}>
                      <div>{formatAttendanceHistoryDate(row)}</div>
                      <div>
                        <span className={`new-hrm-attendance-log__status new-hrm-attendance-log__status--${row.tone}`} title={row.label}>
                          <span aria-hidden="true"></span>
                          {row.code}
                        </span>
                      </div>
                      <div className="new-hrm-attendance-row-actions">
                        {isActionableUnknownAttendanceRow(row, todayKey) && (
                          <>
                            <button type="button" onClick={() => openAttendanceAction("leave")} disabled={dataLoading}>
                              <FiCalendar aria-hidden="true" />
                              Apply Leave
                            </button>
                            <button type="button" onClick={() => openAttendanceAction("attendanceRequest")} disabled={dataLoading}>
                              <FiFileText aria-hidden="true" />
                              Raise Case
                            </button>
                          </>
                        )}
                      </div>
                    </React.Fragment>
                  ))
                ) : (
                  <div className="new-hrm-attendance-log__empty">No attendance found for this month.</div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

export default LeaveScreen;
